import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Lock } from "lucide-react";
import useFeatureAccess from "@/hooks/useFeatureAccess";

export const SurgePricingManagement = () => {
  const { canSaveFeature } = useFeatureAccess();
  const { canSave, reason } = canSaveFeature("surge_pricing");
  
  const [surge, setSurge] = useState({ multiplier: 1.0, is_active: false, reason: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSurge = async () => {
      try {
        const res = await axios.get(`${API}/surge-pricing`);
        setSurge(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSurge();
  }, []);

  const saveSurge = async () => {
    if (!canSave) {
      toast.error(reason || "This feature is not available");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/surge-pricing`, surge);
      toast.success("Surge pricing updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update surge pricing");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="surge-pricing-management">
      <h1 className="text-2xl font-bold text-foreground mb-2">Surge Pricing</h1>
      <p className="text-muted-foreground mb-6">Set dynamic pricing during peak demand</p>

      {/* Restriction Banner */}
      {!canSave && reason && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3 max-w-md">
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">{reason}</p>
            <p className="text-sm text-amber-600">You can view settings but cannot make changes.</p>
          </div>
        </div>
      )}

      <div className="max-w-md space-y-6">
        <div className="flex items-center justify-between p-4 bg-card rounded-xl border border-border">
          <div>
            <h3 className="font-semibold">Enable Surge Pricing</h3>
            <p className="text-sm text-muted-foreground">Apply multiplier to all rides</p>
          </div>
          <Switch checked={surge.is_active} onCheckedChange={(v) => setSurge({...surge, is_active: v})} disabled={!canSave} />
        </div>

        <div>
          <Label>Surge Multiplier</Label>
          <div className="flex items-center gap-4 mt-2">
            <input
              type="range"
              min="1"
              max="3"
              step="0.1"
              value={surge.multiplier}
              onChange={(e) => setSurge({...surge, multiplier: parseFloat(e.target.value)})}
              className="flex-1"
              disabled={!canSave}
            />
            <span className="font-bold text-2xl text-primary">{surge.multiplier.toFixed(1)}x</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Fare = Base fare × {surge.multiplier.toFixed(1)}</p>
        </div>

        <div>
          <Label>Reason (shown to customers)</Label>
          <Input value={surge.reason || ""} onChange={(e) => setSurge({...surge, reason: e.target.value})} placeholder="e.g., High demand, Peak hours" disabled={!canSave} />
        </div>

        <Button onClick={saveSurge} disabled={saving || !canSave} className="w-full">
          {!canSave && <Lock className="w-4 h-4 mr-2" />}
          {saving ? "Saving..." : "Save Surge Settings"}
        </Button>
      </div>
    </div>
  );
};

export default SurgePricingManagement;
